import CloseIcon from "@mui/icons-material/Close";
import type { SxProps, Theme } from "@mui/material";
import { AppBar, Box, Drawer, IconButton, SwipeableDrawer, Toolbar, Typography, useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";
import { INK, INK_BLACK, SURFACE } from "@/theme/tokens";
import { SheetDragHandleContext } from "./SheetDragHandleContext";
import { useSheetDragToClose } from "./useSheetDragToClose";

/** Height of the peek header that bleeds above the drawer when closed */
export const PEEK_HEIGHT = 76;

/** A bare number means px (React's convention) — `calc()` needs the unit spelled out. */
const toCssLength = (value: string | number): string => (typeof value === "number" ? `${value}px` : value);

type InHouseMobileSheetProps = {
	open: boolean;
	onClose: () => void;
	onOpen?: () => void;
	title?: ReactNode;

	/** Default: 92vh */
	height?: string | number;

	/** Use the requested height at tablet widths too (the usual desktop sheet is capped at 80vh). */
	fullViewport?: boolean;

	/** Size the sheet to its content up to maxHeight instead of forcing a fixed viewport height. */
	fitContent?: boolean;

	/** Used with fitContent. Default: 86dvh */
	maxHeight?: string | number;

	/** If true, shows the top AppBar with close button */
	showHeader?: boolean;

	/** Show the grab handle. Only meaningful while the sheet is actually draggable — see `swipeToClose`. */
	showHandle?: boolean;

	/**
	 * Opt into MUI's drag-to-close. OFF by default because `SwipeableDrawer` calls `preventDefault()` on
	 * every touchmove over its paper, which cancels native scrolling of anything inside it — a sheet with
	 * a scrollable body cannot have both. `peekWhenClosed` implies it (the peek edge needs the gesture).
	 */
	swipeToClose?: boolean;

	/**
	 * Drag down from the grab handle — and from a `SheetStep` heading inside the sheet — to close, while everything else
	 * keeps native scrolling (`useSheetDragToClose`). The way to make a SCROLLING sheet draggable, which `swipeToClose`
	 * cannot be. Shows the handle. Ignored with `swipeToClose` / `peekWhenClosed`, which already drag the whole paper.
	 */
	dragHandleToClose?: boolean;

	/** If true, shows a peek header at the bottom of the screen when closed. Users can swipe or tap to re-open. */
	peekWhenClosed?: boolean;

	/** Optional footer (sticky) */
	footer?: ReactNode;

	children: ReactNode;

	/**
	 * Lifts the sheet off the bottom edge by this CSS length, so it stops ABOVE a bar pinned there (a phone bottom
	 * navigation) instead of sliding under it. Moves the paper AND the scrim together: a scrim left full-height would
	 * dim the bar and swallow its taps, and a paper left at the edge would hide its last row behind it. Only moves
	 * the sheet — size it for the room above the bar with `height` / `maxHeight`. Absent = the viewport's bottom edge.
	 */
	bottomOffset?: string;

	/**
	 * The layer the whole sheet (paper and scrim) sits on. Absent = MUI's drawer layer, under dialogs. A sheet that is
	 * a transient pick opened FROM another surface — a dropdown's options on a phone — passes its popover's layer, so it
	 * covers whatever it was opened from (a dialog, another sheet, a bar pinned to the bottom) instead of opening behind it.
	 */
	zIndex?: number;

	/** Extra styling */
	paperSx?: SxProps<Theme>;
	contentSx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * A bottom sheet for mobile devices.
 *
 * Draggable only when `peekWhenClosed` or `swipeToClose` is set — otherwise it renders a plain `Drawer`,
 * because `SwipeableDrawer` suppresses native scrolling inside the sheet (see `swipeToClose`).
 * When `peekWhenClosed` is true the drawer stays mounted and shows a visible header at the bottom using
 * SwipeableDrawer's native edge/bleeding pattern.
 */
export function InHouseMobileSheet({
	open,
	onClose,
	title,
	height = "92vh",
	fullViewport,
	fitContent = false,
	maxHeight = "86dvh",
	showHeader = true,
	showHandle = true,
	peekWhenClosed = false,
	swipeToClose = false,
	// DEFAULT: off — every existing sheet keeps its gesture (or lack of one) exactly as it was.
	dragHandleToClose = false,
	footer,
	children,
	paperSx,
	contentSx,
	onOpen,
	bottomOffset,
	zIndex,
	dataTestId,
}: InHouseMobileSheetProps) {
	const isMobile = useMediaQuery("(max-width:600px)");
	const dragHandle = useSheetDragToClose(onClose);

	// For desktop: you can still use it as a bottom sheet,
	// but you can also choose to render a Dialog instead if you want.
	// Keeping it simple: always drawer, but slightly smaller on desktop.
	// Normalized before it reaches `calc()` below: `height`/`maxHeight` accept a number (React's px
	// convention), and `calc(320 - 56px)` is invalid CSS — the browser drops the whole declaration, so
	// the body loses its height and a fixed sheet clips while a `fitContent` one never gains its scroll
	// cap. CSS strings (vh/dvh/%/calc) pass through untouched.
	const finalHeight = toCssLength(fullViewport === true || isMobile ? height : "80vh");
	const finalMaxHeight = toCssLength(fullViewport === true || isMobile ? maxHeight : "80vh");

	// The grab handle and the SwipeableDrawer branch move together: an undraggable sheet must not show a
	// handle promising a gesture that was deliberately given up for working inner scroll.
	const isDraggable = peekWhenClosed || swipeToClose;
	const isHandleDraggable = dragHandleToClose && !isDraggable;
	// Reserve the handle's 24px only when the handle is actually RENDERED (below, same condition). Keying
	// this off `showHandle` alone — which defaults to true — cost every undraggable sheet 24px of body for
	// a handle that isn't there: dead space in a fixed sheet, lost scroll room in a fit-content one.
	const handleOffset = (showHandle && isDraggable) || isHandleDraggable ? "24px" : "0px";
	const fixedBodyHeight = `calc(${finalHeight} - ${handleOffset} - ${showHeader ? "56px" : "0px"})`;
	const fitContentBodyMaxHeight = `calc(${finalMaxHeight} - ${handleOffset} - ${showHeader ? "56px" : "0px"})`;

	const paperSxResolved = {
		height: fitContent ? "auto" : finalHeight,
		maxHeight: fitContent ? finalMaxHeight : undefined,
		borderTopLeftRadius: peekWhenClosed && !open ? 0 : 16,
		borderTopRightRadius: peekWhenClosed && !open ? 0 : 16,
		overflow: peekWhenClosed ? "visible" : "hidden",
		...(bottomOffset === undefined ? {} : { bottom: bottomOffset }),
		...paperSx,
	};
	// Spread only when set, so a sheet with no offset hands MUI exactly the slot props it always did.
	const backdropSlot = bottomOffset === undefined ? {} : { backdrop: { sx: { bottom: bottomOffset } } };
	// Same reason: no `sx` on the root at all unless a layer was asked for.
	const rootSx = zIndex === undefined ? undefined : { zIndex };

	const sheetBody = (
		<>
			{peekWhenClosed && !open && (
				<Box
					sx={{
						position: "absolute",
						top: -PEEK_HEIGHT,
						left: 0,
						right: 0,
						bgcolor: SURFACE,
						borderTopLeftRadius: 16,
						borderTopRightRadius: 16,
						visibility: "visible",
						boxShadow: `0px -2px 8px ${alpha(INK_BLACK, 0.12)}`,
					}}
				>
					<Box sx={{ py: 1, display: "flex", justifyContent: "center" }}>
						<Box sx={{ width: 44, height: 5, borderRadius: 999, bgcolor: "grey.400" }} />
					</Box>
					<Toolbar sx={{ minHeight: "56px !important", px: 2 }}>
						{title && (
							<Typography variant="h6" component="div" sx={{ fontSize: "1rem", color: INK.PRIMARY }}>
								{title}
							</Typography>
						)}
					</Toolbar>
				</Box>
			)}

			{showHandle && open && isDraggable && (
				<Box sx={{ py: 1, display: "flex", justifyContent: "center" }}>
					<Box sx={{ width: 44, height: 5, borderRadius: 999, bgcolor: "grey.400" }} />
				</Box>
			)}

			{isHandleDraggable && open && (
				<Box
					{...dragHandle}
					data-testid={dataTestId === undefined ? undefined : `${dataTestId}-drag-handle`}
					sx={{ py: 1, display: "flex", justifyContent: "center", cursor: "grab", "&:active": { cursor: "grabbing" } }}
				>
					<Box sx={{ width: 44, height: 5, borderRadius: 999, bgcolor: "grey.400" }} />
				</Box>
			)}

			{showHeader && open && (
				<AppBar
					sx={{
						position: "relative",
						bgcolor: SURFACE,
						color: INK.PRIMARY,
						boxShadow: "none",
						borderBottom: "1px solid",
						borderColor: "divider",
					}}
				>
					<Toolbar sx={{ minHeight: "56px !important" }}>
						<IconButton edge="start" color="inherit" onClick={onClose} aria-label="close" sx={{ mr: 1 }}>
							<CloseIcon />
						</IconButton>
						{title && (
							<Typography variant="h6" component="div" sx={{ fontSize: "1rem" }}>
								{title}
							</Typography>
						)}
					</Toolbar>
				</AppBar>
			)}

			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					minHeight: 0,
					...(fitContent ? { maxHeight: fitContentBodyMaxHeight } : { height: fixedBodyHeight }),
				}}
			>
				{/* `fitContent` used to mean `overflow: visible` — i.e. no scroller at all, so content past
				    `maxHeight` was clipped by the paper and unreachable. `flex: 0 1 auto` + `minHeight: 0`
				    still sizes to content, so the sheet only scrolls once it is actually height-capped. */}
				<Box sx={{ flex: fitContent ? "0 1 auto" : 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", p: 2, ...contentSx }}>
					<SheetDragHandleContext.Provider value={isHandleDraggable ? dragHandle : null}>{children}</SheetDragHandleContext.Provider>
				</Box>

				{footer && <Box sx={{ borderTop: "1px solid", borderColor: "divider", bgcolor: "background.paper", p: 2 }}>{footer}</Box>}
			</Box>
		</>
	);

	// SwipeableDrawer preventDefault()s every touchmove over the paper to drive its drag-to-close, which
	// cancels native scrolling of anything inside it. Only `peekWhenClosed` needs the swipe.
	if (!isDraggable) {
		return (
			<Drawer
				data-testid={dataTestId}
				anchor="bottom"
				open={open}
				onClose={onClose}
				sx={rootSx}
				slotProps={{ paper: { sx: paperSxResolved }, ...backdropSlot }}
			>
				{sheetBody}
			</Drawer>
		);
	}

	return (
		<SwipeableDrawer
			data-testid={dataTestId}
			anchor="bottom"
			open={open}
			onClose={onClose}
			sx={rootSx}
			onOpen={() => {
				onOpen?.();
			}}
			disableSwipeToOpen={!peekWhenClosed}
			disableDiscovery={!peekWhenClosed}
			swipeAreaWidth={peekWhenClosed ? PEEK_HEIGHT : 24}
			ModalProps={peekWhenClosed ? { keepMounted: true } : undefined}
			slotProps={{
				swipeArea: peekWhenClosed ? { onClick: () => onOpen?.() } : undefined,
				paper: { sx: paperSxResolved },
				...backdropSlot,
			}}
		>
			{sheetBody}
		</SwipeableDrawer>
	);
}
